import React, { useEffect, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import { auth, database } from "firebase/app";
import { Box, Button, Flex, Heading, Text } from "rebass";
import { FaFileUpload } from "react-icons/fa";
import { MdLaptopMac, MdNavigateBefore } from "react-icons/md";

import { StyledQRCode } from "../../components/styled/QRCode";
import { SpinnerSVG } from "../../components/svg/SpinnerSVG";

import { getUser } from "../../../store/modules/session/selectors";

const STEPS = [
  "Open Speechify on your phone",
  <>
    <span>Tap <b>Import</b></span>
    &nbsp;
    <Text as="span" fontSize="18px" color="sp-blue"><FaFileUpload display="block" /></Text>
  </>,
  <>
    <span>Tap <b>Connect to Desktop</b></span>
    &nbsp;
    <Text as="span" fontSize="18px" color="sp-blue"><MdLaptopMac display="block" /></Text>
  </>,
  <><span>Tap <b>open camera</b></span></>,
];

export const SignInWithQR: React.FC<RouteComponentProps> = ({ history }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const user = useSelector(getUser);
  const { push } = useHistory();

  useEffect(() => {
    if (user && user.isAnonymous) {
      const tokenRef = database().ref(`tokens/${user.uid}`);
      const callback = (snapshot: database.DataSnapshot) => {
        if (snapshot.exists()) {
          setIsProcessing(true);

          auth().signInWithCustomToken(snapshot.val())
            .then(() => {
              push("/");
            })
            .catch((error: Error) => {
              toastr.error("Firebase Sign-In", error.message);
              setIsProcessing(false);
            });
        }
      };

      tokenRef.on("value", callback);

      return () => {
        tokenRef.off("value", callback);
      };
    }
  }, [user, push]);

  return (
    <Flex
      height="100%"
      flexDirection="column"
      p="65px 100px 35px"
    >
      <Flex color="#2b3160">
        <Button
          px={0}
          py={0}
          mr="15px"
          borderRadius="50%"
          color="inherit"
          bg="hoki-25"
          onClick={history.goBack}
        >
          <MdNavigateBefore size={32} display="block" />
        </Button>

        <Heading
          as="h3"
          fontSize="30px"
          fontWeight="bold"
          lineHeight={1.07}
        >
          Sign in with QR code
        </Heading>
      </Flex>

      <Box
        width="75%"
        mt="20px"
        mx="auto"
        css="position: relative;"
      >
        <Box css={`transition: opacity .4s; opacity: ${isProcessing ? ".2" : "1"};`}>
          {
            (user && user.isAnonymous) && (
              <StyledQRCode
                value={user.uid}
                renderAs="svg"
                bgColor="transparent"
              />
            )
          }
        </Box>

        {
          isProcessing && (
            <Flex
              justifyContent="center"
              alignItems="center"
              color="sp-blue"
              css="position: absolute; top: 0; left: 0; right: 0; bottom: 0;"
            >
              <SpinnerSVG size={40} />
            </Flex>
          )
        }
      </Box>

      <Box mt="16px">
        <Heading
          as="h4"
          fontSize="24px"
          fontWeight="bold"
          lineHeight={1.07}
          color="#2b3160"
        >
          To use Speechify on your computer:
        </Heading>

        <Box
          as="ol"
          mt="10px"
          color="#2b3160"
        >
          {
            STEPS.map((step, i) => (
              <Box key={i} as="li">
                <Text as="p" css="display: inline-flex;">{step}</Text>
              </Box>
            ))
          }
        </Box>
      </Box>
    </Flex>
  );
};
